import React, { useEffect } from 'react';
import { Autocomplete, TextField, Skeleton } from '@mui/material';
import { useApiGet } from '../utils/httpClient';
import { getChapters } from '../endpoints/annualReports';

interface ChapterObject {
  id: string;
  search_name: string;
  [key: string]: any; // Allow additional properties
}

// Base props interface
// keyId: instance identifier for React key (e.g. page name, entity id).
interface BaseChapterSelectorProps {
  chapter: string;
  spd: string | null;
  keyId: string;
  label?: string;
  all?: boolean;
}

// When objectReturned is true, handler receives an object
interface ChapterSelectorPropsWithObject extends BaseChapterSelectorProps {
  objectReturned: true;
  handleChapterSelect: (value: ChapterObject | null) => void;
}

// When objectReturned is false (default), handler receives a string
interface ChapterSelectorPropsWithString extends BaseChapterSelectorProps {
  objectReturned?: false;
  handleChapterSelect: (value: string | null) => void;
}

type ChapterSelectorProps =
  | ChapterSelectorPropsWithObject
  | ChapterSelectorPropsWithString;

const ChapterSelector: React.FC<ChapterSelectorProps> = ({
  chapter,
  handleChapterSelect,
  spd,
  keyId,
  label = 'Chapter',
  objectReturned = false,
  all = false,
}) => {
  const { data } = useApiGet(
    ['chapters', spd, all, keyId],
    getChapters,
    {
      enabled: true,
      retry: 1,
    },
    { spd_id: spd, all: all }
  );

  const chapterList = data as ChapterObject[] | undefined;

  useEffect(() => {
    if (chapterList && chapterList.length === 1 && !chapter) {
      (handleChapterSelect as (value: string) => void)(chapterList[0].id);
    }
  }, [chapterList, chapter, handleChapterSelect]);

  const onChapterSelect = (chapterId: string | null) => {
    if (objectReturned && chapterId) {
      const chapterObject = chapterList?.find(
        (chapterObj) => chapterObj.id === chapterId
      );
      (handleChapterSelect as (value: ChapterObject | null) => void)(
        chapterObject || null
      );
    } else {
      (handleChapterSelect as (value: string | null) => void)(chapterId);
    }
  };

  const getValue = (): ChapterObject | null => {
    if (chapter && chapterList) {
      const chap = chapterList.find(
        (chapterObject) => chapterObject.id === chapter
      );
      if (chap) {
        return chap;
      } else {
        onChapterSelect(null);
        return null;
      }
    }
    return null;
  };

  return chapterList ? (
    <Autocomplete
      key={keyId}
      options={chapterList}
      size="small"
      clearOnBlur={true}
      getOptionLabel={(option) => option.search_name}
      value={getValue()}
      onChange={(event, newValue) =>
        onChapterSelect(newValue ? newValue.id : null)
      }
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  ) : (
    <Skeleton>
      <TextField label={label} />
    </Skeleton>
  );
};

export default ChapterSelector;
